import { RefObject, useCallback, useEffect, useRef, useState } from 'react';

type UseStickyBottomReturn = {
  ref: RefObject<HTMLDivElement | null>;
  style: React.CSSProperties;
};

/** Default header height in pixels (9.5rem) */
const DEFAULT_HEADER_HEIGHT = 152;
/** Gap between header and content in pixels (2rem) */
const DEFAULT_HEADER_CONTENT_GAP = 32;
const STUDY_HEADER_CONTENT_GAP = 24;

/** Debounce delay for MutationObserver callbacks (ms) */
const MUTATION_DEBOUNCE_MS = 50;
const STICKY_HEADER_CONFIGS = [
  { contentGap: DEFAULT_HEADER_CONTENT_GAP, selector: '[data-tech-header="true"]' },
  { contentGap: STUDY_HEADER_CONTENT_GAP, selector: '[data-meia-study-header="true"]' },
];

type StickyHeaderConfig = {
  contentGap: number;
  element: HTMLElement | null;
};

type StickyTopValueParams = {
  availableHeight: number;
  elementHeight: number;
  topOffset: number;
};

export function calculateStickyTopValue(params: StickyTopValueParams): number {
  const { availableHeight, elementHeight, topOffset } = params;

  if (elementHeight <= availableHeight) {
    return topOffset;
  }

  return -(elementHeight - availableHeight);
}

function getStickyHeaderConfig(): StickyHeaderConfig {
  for (const { contentGap, selector } of STICKY_HEADER_CONFIGS) {
    const element = document.querySelector<HTMLElement>(selector);
    if (element) return { contentGap, element };
  }

  return { contentGap: DEFAULT_HEADER_CONTENT_GAP, element: null };
}

/**
 * Gets the current top offset accounting for header and impersonation banner.
 * Reads --header-height CSS variable with fallback to default.
 */
function getTopOffset(): number {
  const root = document.documentElement;
  const style = getComputedStyle(root);

  const { element: headerElement } = getStickyHeaderConfig();
  const headerBottom = headerElement?.getBoundingClientRect().bottom;
  if (headerBottom !== undefined && headerBottom > 0) {
    return headerBottom;
  }

  const measuredHeaderHeight = headerElement?.offsetHeight;

  // parseFloat handles "152px" -> 152 automatically
  const headerHeightValue = style.getPropertyValue('--header-height').trim();
  const headerHeight =
    measuredHeaderHeight ?? (headerHeightValue ? parseFloat(headerHeightValue) : DEFAULT_HEADER_HEIGHT);

  const bannerHeightValue = style.getPropertyValue('--impersonation-banner-height').trim();
  const bannerHeight = bannerHeightValue ? parseFloat(bannerHeightValue) : 0;

  return headerHeight + bannerHeight;
}

/**
 * Hook for sticky column behavior where shorter columns stick when their
 * bottom reaches the viewport bottom.
 *
 * Uses a calculated negative `top` value technique:
 * - If column fits in viewport: sticks at top normally
 * - If column is taller: uses negative top so it sticks when bottom reaches viewport bottom
 *
 * Recalculates on:
 * - Initial mount (after animation completes)
 * - Window resize
 * - Element size changes (ResizeObserver)
 * - DOM mutations (MutationObserver, debounced)
 * - First scroll (fallback for late-loading content)
 */
export function useStickyBottom(): UseStickyBottomReturn {
  const ref = useRef<HTMLDivElement>(null);
  const [stickyTop, setStickyTop] = useState<number>(DEFAULT_HEADER_HEIGHT);
  const hasCalculatedOnScroll = useRef(false);
  const pendingRafId = useRef<number | null>(null);
  const stickyTopRef = useRef<number>(DEFAULT_HEADER_HEIGHT);

  const calculateStickyTop = useCallback(() => {
    const element = ref.current;
    if (!element) return;

    const { contentGap } = getStickyHeaderConfig();
    const topOffset = getTopOffset() + contentGap;
    const elementHeight = element.offsetHeight;
    const viewportHeight = window.innerHeight;
    const availableHeight = viewportHeight - topOffset;
    const nextStickyTop = calculateStickyTopValue({ availableHeight, elementHeight, topOffset });

    if (stickyTopRef.current !== nextStickyTop) {
      stickyTopRef.current = nextStickyTop;
      setStickyTop(nextStickyTop);
    }
  }, []);

  const scheduleStickyTopCalculation = useCallback(() => {
    if (pendingRafId.current !== null) return;

    pendingRafId.current = requestAnimationFrame(() => {
      pendingRafId.current = null;
      calculateStickyTop();
    });
  }, [calculateStickyTop]);

  // Scroll-based recalculation as fallback for late-loading content
  useEffect(() => {
    const handleScroll = () => {
      if (!hasCalculatedOnScroll.current) {
        calculateStickyTop();
        hasCalculatedOnScroll.current = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [calculateStickyTop]);

  useEffect(() => {
    // Initial calculation with delay to wait for AnimatePresence animation (200ms) to complete
    const timeoutId = setTimeout(calculateStickyTop, 250);

    // Recalculate on resize
    window.addEventListener('resize', scheduleStickyTopCalculation);

    const element = ref.current;
    const { element: headerElement } = getStickyHeaderConfig();
    let resizeObserver: ResizeObserver | null = null;
    let headerResizeObserver: ResizeObserver | null = null;
    let mutationObserver: MutationObserver | null = null;
    let rootMutationObserver: MutationObserver | null = null;
    let mutationTimeout: number | undefined;

    // Observe element size changes
    if (element && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        scheduleStickyTopCalculation();
      });
      resizeObserver.observe(element);
    }

    if (headerElement && typeof ResizeObserver !== 'undefined') {
      headerResizeObserver = new ResizeObserver(() => {
        scheduleStickyTopCalculation();
      });
      headerResizeObserver.observe(headerElement);
    }

    // Observe DOM mutations (for async content loading) with debounce
    if (element && typeof MutationObserver !== 'undefined') {
      mutationObserver = new MutationObserver(() => {
        // Debounce to prevent excessive recalculations
        clearTimeout(mutationTimeout);
        mutationTimeout = window.setTimeout(() => {
          scheduleStickyTopCalculation();
        }, MUTATION_DEBOUNCE_MS);
      });
      mutationObserver.observe(element, { childList: true, subtree: true });
    }

    if (typeof MutationObserver !== 'undefined') {
      rootMutationObserver = new MutationObserver(() => {
        scheduleStickyTopCalculation();
      });
      rootMutationObserver.observe(document.documentElement, { attributeFilter: ['style'], attributes: true });
    }

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(mutationTimeout);
      if (pendingRafId.current !== null) {
        cancelAnimationFrame(pendingRafId.current);
        pendingRafId.current = null;
      }
      window.removeEventListener('resize', scheduleStickyTopCalculation);
      headerResizeObserver?.disconnect();
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      rootMutationObserver?.disconnect();
    };
  }, [calculateStickyTop, scheduleStickyTopCalculation]);

  const style: React.CSSProperties = {
    alignSelf: 'flex-start',
    position: 'sticky',
    top: stickyTop,
  };

  return { ref, style };
}
